import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { observeConsentLanguage } from "./consent";
import { useLocation } from "react-router-dom";
import { getLocaleFromPath, localizePath, type Locale } from "./locale";

interface LocaleValue {
  locale: Locale;
  /** Localize an internal path for the active locale. */
  lp: (to: string) => string;
  /** Pick the value for the active locale from an { en, es } pair. */
  pick: <T>(v: { en: T; es: T }) => T;
}

const LocaleCtx = createContext<LocaleValue | null>(null);

function build(locale: Locale): LocaleValue {
  return {
    locale,
    lp: (to) => localizePath(to, locale),
    pick: (v) => v[locale],
  };
}

/** Derives locale from the current URL - must be rendered inside the router. */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const locale = getLocaleFromPath(pathname);
  const value = useMemo(() => build(locale), [locale]);
  useEffect(() => observeConsentLanguage(locale === "es"), [locale]);
  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleCtx);
  // Outside a provider (tests, isolated renders) default to English.
  return ctx ?? build("en");
}

/** Convenience: select a translation catalog for the active locale. */
export function useCopy<T>(catalog: { en: T; es: T }): T {
  return catalog[useLocale().locale];
}
